import { useEffect } from 'react'

import { useRequest } from 'ahooks'

import { queryFileDetailApi } from '@eastcoal/api/nexus/oss/queryFileDetail'

import { ObjectAcl } from '@eastcoal/common/enums/ObjectAcl'
import { KeySet } from '@eastcoal/common/types/utilities'
import { wrapUploadFileApi } from '@eastcoal/common/utils/upload'

import { useAttachmentCardStoreApi } from '../hooks/useStore'
import { AttachmentCardState } from '../store/interface'

import { AttachmentCardStoreUpdaterProps } from './interface'

const useStoreUpdater = <K extends KeySet<AttachmentCardState>>(
  key: K,
  value: AttachmentCardState[K]
) => {
  const { setState } = useAttachmentCardStoreApi()
  useEffect(() => {
    setState({ [key]: value })
  }, [key, value, setState])
}

const requstConfig = {
  timeout: 5 * 60 * 1000
}

export const AttachmentCardStoreUpdater = ({
  nodeKey,
  file,
  ossId: externalOssId,
  ossUrl: externalOssUrl,
  filename: externalFilename,
  mimeType: externalMimeType,
  error: externalError,
  loading: externalLoading
}: AttachmentCardStoreUpdaterProps) => {
  const {
    data: uploadData,
    error: uploadError,
    loading: uploadLoading
  } = useRequest(
    () =>
      wrapUploadFileApi(
        { file, objectAcl: ObjectAcl.PUBLIC_READ },
        requstConfig
      ),
    {
      manual: false,
      ready: !!file && !externalOssId
    }
  )
  // 外部传入了OssUrl就不需要请求文件详情
  const {
    data: queryData,
    error: queryError,
    loading: queryLoading
  } = useRequest(() => queryFileDetailApi({ ossId: externalOssId }), {
    manual: false,
    ready: !externalOssUrl && !file && !!externalOssId,
    cacheKey: externalOssId,
    staleTime: Infinity
  })

  const data = uploadData ?? queryData
  const error = uploadError ?? queryError
  const loading = uploadLoading ?? queryLoading

  const ossId = data?.ossId ?? externalOssId
  const ossUrl = externalOssUrl ?? data?.accessUrl
  const filename = file?.name ?? data?.filename ?? externalFilename
  const mimeType = data?.mimeType ?? externalMimeType

  useStoreUpdater('nodeKey', nodeKey)
  useStoreUpdater('ossId', ossId)
  useStoreUpdater('ossUrl', ossUrl)
  useStoreUpdater('filename', filename)
  useStoreUpdater('mimeType', mimeType)
  useStoreUpdater('error', error?.message ?? externalError)
  useStoreUpdater('loading', loading ?? externalLoading)

  return null
}
